import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { GroupTranslatorProvider, useGroupTranslator } from '../../components/i18n/GroupTranslationContext';

// Components
import ColorBar from '../../components/colors/ColorBar';
import ElementUserTag from '../../user/userActivity/ElementUserTag';
import LinkMedia from './richMedia/LinkMedia';
import ElementResizeHandle from '../resizing/ElementResizeHandle';
import ElementActivityIndicator from '../activity/ElementActivityIndicator';
import QuickNotesActivityIndicator from '../activity/QuickNotesActivityIndicator';
import QuickLineCreationTool from '../quickLine/QuickLineCreationTool';
import PillsPanel from '../pillsPanel/PillsPanel';
import AttachedCommentsDropTarget from '../dnd/elementDropTargets/AttachedCommentsDropTarget';
import AttachedCommentsContainer from '../attachedComment/AttachedCommentsContainer';
import LinkMetaSection from './LinkMetaSection';

// Helpers
import { getElementStyle } from '../../utils/grid/gridUtils';
import {
    getCaption,
    getColor,
    getElementId,
    getLinkUrl,
    getShowMedia,
    getWidth,
} from '../../../common/elements/utils/elementPropertyUtils';
import { getShowQuickLineTool } from '../quickLine/quickLineUtil';
import { getIsReplaceModeHovered } from '../../reducers/draggingSelector';
import { elementClassNames, getDomElementId } from '../utils/elementUtil';
import {
    getShouldApplyMediaOnlyDisplay,
    getShouldShowElementCaptionMediaOptional,
} from '../media/mediaElementDisplayUtils';
import { getIsLinkMediaVisible } from './linkUtils';

// Constants
import { ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';

// Styles
import './Link.scss';

const LinkContent = (props) => {
    const groupTranslate = useGroupTranslator();
    const {
        currentBoardId,
        elementId,
        element,
        inList,
        gridSize,
        elementEvents,
        tempSize,
        isLocked,
        isEditable,
        isSelected,
        isPresentational,
        permissions,
        setParentHoveredChildAcceptsDrop,
        getContextZoomScale,
        getContextZoomTranslationPx,
    } = props;

    const colorName = getColor(element);

    const isReplaceModeHovered = useSelector((state) => getIsReplaceModeHovered(state, { elementId }));

    const showMedia = getShowMedia(element);
    const isMediaVisible = getIsLinkMediaVisible(element);
    const shouldDisplayMediaOnly = isMediaVisible && getShouldApplyMediaOnlyDisplay(props);

    const captionContent = getCaption(element);
    const captionVisible = getShouldShowElementCaptionMediaOptional(props, isMediaVisible);

    const isLoaded = !!getLinkUrl(element);

    const canResize = !isLocked && isEditable && !inList;
    const elementWidth = tempSize?.width || getWidth(element) || ELEMENT_DEFAULT_WIDTH;

    const linkClasses = classNames('Link drag-handle', { 'contrast-shadow': !showMedia || !isLoaded });
    const classes = elementClassNames(linkClasses, { ...props, isReplaceModeHovered });

    return (
        <div
            id={getDomElementId(getElementId(element))}
            className={classes}
            style={getElementStyle(elementWidth, gridSize, inList)}
            {...elementEvents}
        >
            <LinkMedia {...props} width={elementWidth} />
            <ColorBar colorName={colorName} isSelected={isSelected} />
            <QuickLineCreationTool
                show={getShowQuickLineTool(props)}
                elementId={elementId}
                element={element}
                currentBoardId={currentBoardId}
                gridSize={gridSize}
                getContextZoomScale={getContextZoomScale}
                getContextZoomTranslationPx={getContextZoomTranslationPx}
            />
            <ElementActivityIndicator {...props} />
            <QuickNotesActivityIndicator {...props} />
            <ElementUserTag {...props} />
            {!shouldDisplayMediaOnly && (
                <LinkMetaSection
                    {...props}
                    isMediaVisible={isMediaVisible}
                    placeholder={groupTranslate('caption.placeholder')}
                    textContent={captionContent}
                    captionVisible={captionVisible}
                />
            )}
            <PillsPanel {...props} />
            <AttachedCommentsDropTarget
                setParentHoveredChildAcceptsDrop={setParentHoveredChildAcceptsDrop}
                isRelative
                gridSize={gridSize}
                isPresentational={isPresentational}
                element={element}
                elementId={elementId}
                getContextZoomScale={getContextZoomScale}
            />
            <AttachedCommentsContainer
                elementId={elementId}
                element={element}
                gridSize={gridSize}
                isPresentational={isPresentational}
                currentBoardId={currentBoardId}
                permissions={permissions}
            />
            <ElementResizeHandle {...props} showHandle={canResize} />
        </div>
    );
};

LinkContent.propTypes = {
    currentBoardId: PropTypes.string,
    elementId: PropTypes.string,
    element: PropTypes.object.isRequired,
    gridSize: PropTypes.number,
    inList: PropTypes.string,
    isSelected: PropTypes.bool,
    isSingleSelected: PropTypes.bool,
    isEditing: PropTypes.bool,
    isHovered: PropTypes.bool,
    startEditing: PropTypes.func,
    setEditHandler: PropTypes.func,
    stopEditing: PropTypes.func,
    saveContent: PropTypes.func,
    handleKey: PropTypes.func,
    className: PropTypes.string,
    elementEvents: PropTypes.object,
    tempSize: PropTypes.object,
    isLocked: PropTypes.bool,
    isEditable: PropTypes.bool,
    isPresentational: PropTypes.bool,
    permissions: PropTypes.number,
    setParentHoveredChildAcceptsDrop: PropTypes.func,
    getContextZoomScale: PropTypes.func,
    getContextZoomTranslationPx: PropTypes.func,
};

const Link = (props) => {
    return (
        <GroupTranslatorProvider group="elements" keyPrefix="elementTypes.link">
            <LinkContent {...props} />
        </GroupTranslatorProvider>
    );
};

export default Link;
